import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import targetApi from "../../services/targetApi";
import TargetProgressCard from "../../components/target/TargetProgressCard";
import KpiCard from "../../components/target/KpiCard";
import Skeleton from "../../components/common/Skeleton";
import EmptyState from "../../components/common/EmptyState";
import { FaBullseye, FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaPlus } from "react-icons/fa";
import "../../styles/target.css";

/* =========================================================
   Employee: My Target
   GET /api/targets/dashboard/me
   Shows the current active target with progress, KPI tiles
   for all targets, and the full target history for the user.
========================================================= */
function MyTarget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [stats, setStats] = useState(null);
  const [allTargets, setAllTargets] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    targetApi
      .getMyDashboardTarget()
      .then((res) => {
        setCurrentTarget(res.data.current_target);
        setStats(res.data.stats);
        setAllTargets(res.data.all_targets || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const submitPath =
    role === "telesales" ? "/telesales/submit-achievement" : "/salesexec/submit-achievement";

  return (
    <div className="mytarget-page">
      <div className="mytarget-header">
        <div>
          <h2>My Target</h2>
          <p>Track your assigned targets and progress</p>
        </div>
        <button className="btn-primary" onClick={() => navigate(submitPath)}>
          <FaPlus /> Submit Achievement
        </button>
      </div>

      {loading ? (
        <Skeleton.Card />
      ) : currentTarget ? (
        <TargetProgressCard target={currentTarget} />
      ) : (
        <EmptyState
          icon="🎯"
          title="No active target right now"
          subtitle="Your manager hasn't assigned an active target yet."
        />
      )}

      {stats && (
        <div className="kpi-mini-grid">
          <KpiCard label="Total Targets" value={stats.total_targets} icon={<FaBullseye />} accent="indigo" />
          <KpiCard label="Active" value={stats.active_targets} icon={<FaHourglassHalf />} accent="amber" />
          <KpiCard label="Completed" value={stats.completed_targets} icon={<FaCheckCircle />} accent="green" />
          <KpiCard label="Expired" value={stats.expired_targets} icon={<FaTimesCircle />} accent="red" />
        </div>
      )}

      <div className="mytarget-history">
        <h3>Target History</h3>

        {loading ? (
          <Skeleton.Table rows={4} cols={5} />
        ) : allTargets.length === 0 ? (
          <EmptyState icon="📋" title="No targets assigned yet" />
        ) : (
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Completed / Total</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Ends</th>
                </tr>
              </thead>
              <tbody>
                {allTargets.map((t) => (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td>{t.target_type}</td>
                    <td>
                      {t.completed_target} / {t.total_target}
                    </td>
                    <td>{t.progress_percent}%</td>
                    <td>
                      <span className={`status-pill status-${(t.status || "").toLowerCase()}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>{t.end_date ? new Date(t.end_date).toLocaleDateString("en-IN") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTarget;
